import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Archive, ArchiveRestore, Copy, RefreshCw, ImagePlus, LogOut, Sparkles, Theater, Users, LayoutDashboard } from "lucide-react";
import { fmtDate } from "@/lib/format";
import TenantAdminWelcome from "@/pages/TenantAdminWelcome";
import { useConfirm } from "@/components/ConfirmDialog";
import DateInputMDY from "@/components/DateInputMDY";
import ImpersonationBanner from "@/components/ImpersonationBanner";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function Productions() {
  const { user, selectProduction, logout } = useAuth();
  const nav = useNavigate();
  const confirm = useConfirm();
  const isAdmin = user?.role === "org_admin" || user?.role === "super_admin";
  const [productions, setProductions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "", tagline: "", venue: "", opening_night: "", director: "" });
  const [showWelcome, setShowWelcome] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/productions", { params: { include_archived: showArchived } })
    .then((r) => { setProductions(r.data); setLoaded(true); });
  useEffect(() => { load(); }, [showArchived]);

  // First-login welcome wizard for tenant admins: fires once we know the
  // productions list and the user is a fresh admin with nothing set up yet.
  useEffect(() => {
    if (!loaded || !user || user.role !== "org_admin") return;
    if (user.needs_setup && productions.length === 0) {
      setShowWelcome(true);
    }
  }, [loaded, user, productions.length]);

  const enter = (p) => {
    selectProduction(p.id);
    nav("/dashboard");
  };

  const createProd = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const r = await api.post("/productions", form);
    setOpenNew(false);
    setForm({ title: "", tagline: "", venue: "", opening_night: "", director: "" });
    toast.success("Production created");
    load();
    // auto-enter the newly created production
    setTimeout(() => enter(r.data), 400);
  };

  const setStatus = async (p, status) => {
    if (status === "archived") {
      const ok = await confirm({
        title: `Archive "${p.title}"?`,
        body: "Once archived, cast and staff will no longer be able to see or open this production. Only admins can restore it.",
        confirmLabel: "Archive",
        tone: "danger",
      });
      if (!ok) return;
    }
    await api.patch(`/productions/${p.id}`, { status });
    toast.success(status === "archived" ? "Archived — hidden from staff and cast" : "Restored");
    load();
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard?.writeText(code)?.catch(() => {});
    } catch (_) { /* clipboard unavailable */ }
    toast.success(`Code ${code} copied`);
  };

  const regenerate = async (p) => {
    const r = await api.post(`/productions/${p.id}/regenerate-code`);
    toast.success(`New code: ${r.data.enroll_code}`);
    load();
  };

  const uploadThumb = async (p, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/productions/${p.id}/thumbnail`, fd);
    toast.success("Thumbnail updated");
    load();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white spotlight-bg">
      <ImpersonationBanner />
      <header className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-white/10 grid place-items-center bg-[#121212]">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-xl leading-none">StageCall</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mt-1">Productions</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isAdmin && (
              <button
                data-testid="goto-admin-dashboard-btn"
                onClick={() => nav("/admin/dashboard")}
                className="h-9 px-3 rounded-md border border-gold/40 hover:border-gold text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5"
              >
                <LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">Admin Dashboard</span><span className="sm:hidden">Admin</span>
              </button>
            )}
            <div className="text-right hidden md:block">
              <div className="text-sm text-white" data-testid="hub-user-name">{user.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user.role}</div>
            </div>
            <button
              data-testid="hub-logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Choose a show</div>
            <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tight font-light">
              {isAdmin ? "All productions" : "Your productions"}
            </h1>
            <p className="mt-2 text-zinc-400 text-sm max-w-2xl">
              {isAdmin
                ? "Manage every show on StageCall. Create a new production, archive old ones, and share enrollment codes with your company."
                : "Enter a production to see your calendar, calls, and notes."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <label className="text-xs text-zinc-400 flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="toggle-archived"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="accent-gold"
                  />
                  Show archived
                </label>
                <Dialog open={openNew} onOpenChange={setOpenNew}>
                  <DialogTrigger asChild>
                    <button
                      data-testid="new-production-btn"
                      style={goldBtn}
                      className="h-10 px-4 rounded-md font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110 transition-all shadow-[0_0_0_1px_rgba(245,158,11,0.4),0_10px_24px_-8px_rgba(245,158,11,0.5)]"
                    >
                      <Plus className="w-4 h-4" /> New production
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-display">Create production</DialogTitle>
                      <DialogDescription className="text-zinc-500">Add a new show. You can upload the poster/logo after creation.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Title</Label><Input data-testid="prod-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                      <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Venue</Label><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                        <div><Label>Director</Label><Input value={form.director} onChange={(e) => setForm({ ...form, director: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                      </div>
                      <div><Label>Opening night</Label><DateInputMDY value={form.opening_night} onChange={(v) => setForm({ ...form, opening_night: v })} /></div>
                    </div>
                    <DialogFooter>
                      <button data-testid="save-production-hub-btn" onClick={createProd} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Create</button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {productions.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/10 p-12 text-center bg-[#0F0F0F]" data-testid="empty-productions">
            <Theater className="w-8 h-8 text-gold mx-auto" />
            <div className="mt-3 font-display text-2xl">{isAdmin ? "No productions yet" : "You're not in a production yet"}</div>
            <div className="text-sm text-zinc-500 mt-1">
              {isAdmin ? "Create your first show to start scheduling rehearsals." : "Ask your stage manager for an enrollment code, then use Self-Enroll on the sign-in page."}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {productions.map((p) => (
            <ProductionCard
              key={p.id}
              production={p}
              isAdmin={isAdmin}
              onEnter={() => enter(p)}
              onArchiveToggle={() => setStatus(p, p.status === "archived" ? "active" : "archived")}
              onCopyCode={() => copyCode(p.enroll_code)}
              onRegenerate={() => regenerate(p)}
              onUploadThumb={(f) => uploadThumb(p, f)}
            />
          ))}
        </div>
      </main>

      {isAdmin && (
        <TenantAdminWelcome
          open={showWelcome}
          onClose={() => setShowWelcome(false)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

function ProductionCard({ production, isAdmin, onEnter, onArchiveToggle, onCopyCode, onRegenerate, onUploadThumb }) {
  const fileRef = useRef(null);
  const archived = production.status === "archived";
  const thumbUrl = production.thumbnail_url
    ? `${process.env.REACT_APP_BACKEND_URL}${production.thumbnail_url}`
    : null;

  return (
    <div
      data-testid={`production-card-${production.id}`}
      className={`group rounded-lg border ${archived ? "border-white/5" : "border-white/10"} bg-[#121212] overflow-hidden hover:border-gold/40 transition-all ${archived ? "opacity-70" : ""}`}
    >
      <div className="relative aspect-[16/9] bg-gradient-to-br from-[#1A1310] via-[#0F0F0F] to-[#0A0A0A] overflow-hidden">
        {thumbUrl ? (
          <img src={thumbUrl} alt={production.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Theater className="w-10 h-10 text-gold/40" />
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {archived && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-white/20 bg-black/60 text-zinc-300">Archived</span>
          )}
          {isAdmin && (
            <button
              data-testid={`upload-thumb-${production.id}`}
              onClick={() => fileRef.current?.click()}
              className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2 rounded border border-white/20 bg-black/60 text-white text-xs inline-flex items-center gap-1 hover:bg-black/80"
            >
              <ImagePlus className="w-3 h-3" /> Poster
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadThumb(f); e.target.value = ""; }}
          />
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display text-xl tracking-tight truncate">{production.title}</div>
          <span className="text-[11px] text-zinc-500 flex items-center gap-1 shrink-0"><Users className="w-3 h-3" />{production.member_count ?? 0}</span>
        </div>
        {production.tagline && <div className="mt-1 text-sm text-zinc-400 line-clamp-2">{production.tagline}</div>}
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
          {production.venue && <span>{production.venue}</span>}
          {production.director && <span>Dir. {production.director}</span>}
          {production.opening_night && <span>Opening · {fmtDate(production.opening_night)}</span>}
        </div>

        {isAdmin && (
          <div className="mt-4 rounded-md border border-white/10 bg-[#0F0F0F] p-3 flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Code</div>
            <code className="font-mono text-gold text-sm tracking-wider" data-testid={`code-${production.id}`}>{production.enroll_code}</code>
            <button data-testid={`copy-code-${production.id}`} onClick={onCopyCode} className="ml-auto p-1.5 rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"><Copy className="w-3.5 h-3.5" /></button>
            <button data-testid={`regen-code-${production.id}`} onClick={onRegenerate} title="Regenerate" className="p-1.5 rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <button
            data-testid={`enter-${production.id}`}
            onClick={onEnter}
            disabled={archived}
            style={goldBtn}
            className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {archived ? "Archived" : "Enter production"}
          </button>
          {isAdmin && (
            <button
              data-testid={`archive-${production.id}`}
              onClick={onArchiveToggle}
              className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
            >
              {archived ? <><ArchiveRestore className="w-4 h-4" /> Restore</> : <><Archive className="w-4 h-4" /> Archive</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
